import { useEffect, useRef, useState, type CSSProperties } from "react";
import { prefersReducedMotion } from "../theme";

/** A blinking caret, shown while crAIg is still writing. */
export function Caret() {
  return (
    <span
      className="blink"
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: "0.5em",
        height: "1em",
        background: "var(--craig)",
        borderRadius: 1,
        verticalAlign: "-0.14em",
        marginLeft: "0.08em",
      }}
    />
  );
}

/**
 * Just enough markdown for what crAIg actually writes: bold, bullets and
 * the occasional heading. A full parser would be more code and more attack
 * surface for output that is deliberately plain.
 */
export function Md({ text, caret }: { text: string; caret?: boolean }) {
  const lines = (text || "").split("\n");

  // The caret belongs on the last line that renders, not the last in the
  // array — trailing blanks are common mid-stream.
  let last = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i]?.trim()) {
      last = i;
      break;
    }
  }

  return (
    <div className="space-y-2">
      {lines.map((ln, i) => {
        const tip = caret && i === last ? <Caret /> : null;
        const parts = ln.split(/\*\*(.*?)\*\*/g).map((part, j) =>
          j % 2 === 1 ? (
            <strong key={j} style={{ fontWeight: 650 }}>
              {part}
            </strong>
          ) : (
            part
          ),
        );

        if (/^\s*[-•]\s/.test(ln)) {
          return (
            <div key={i} className="flex gap-2" style={{ lineHeight: 1.55 }}>
              <span style={{ color: "var(--ink-3)" }}>•</span>
              <span>
                {parts.map((p) => (typeof p === "string" ? p.replace(/^\s*[-•]\s/, "") : p))}
                {tip}
              </span>
            </div>
          );
        }
        if (/^#+\s/.test(ln)) {
          return (
            <p key={i} style={{ fontWeight: 650, paddingTop: 2 }}>
              {ln.replace(/^#+\s/, "")}
              {tip}
            </p>
          );
        }
        if (!ln.trim()) return null;
        return (
          <p key={i} style={{ lineHeight: 1.55 }}>
            {parts}
            {tip}
          </p>
        );
      })}
    </div>
  );
}

/**
 * Types a value out when it first appears.
 *
 * This is the "crAIg is filling your file in" moment, and it earns its
 * keep: without it, values blink into the panel silently and the customer
 * never notices the thing meant to reassure them most.
 */
export function Typed({
  value,
  className,
  style,
}: {
  value: string;
  className?: string;
  style?: CSSProperties;
}) {
  const [shown, setShown] = useState("");

  /*
   * Tracks the value we have FINISHED typing, not the one we started.
   *
   * Guarding on "have I seen this value" breaks under React's StrictMode
   * double-invoke: the first pass marks it seen and schedules, the cleanup
   * cancels, and the second pass exits early — leaving the row blank
   * forever. An interrupted animation has, correctly, finished nothing.
   */
  const done = useRef<string | null>(null);

  useEffect(() => {
    const target = value || "";

    if (!target) {
      done.current = null;
      setShown("");
      return;
    }
    if (done.current === target || prefersReducedMotion()) {
      done.current = target;
      setShown(target);
      return;
    }

    let i = 0;
    setShown("");
    const CHAR_MS = 26;
    // Short answers ("no", "none") finish too fast to register, so hold a
    // beat first — it reads as crAIg pausing to write it down.
    const LEAD_MS = 260;

    let id: ReturnType<typeof setInterval> | undefined;
    const kick = setTimeout(() => {
      id = setInterval(() => {
        i += 1;
        setShown(target.slice(0, i));
        if (i >= target.length) {
          clearInterval(id);
          done.current = target;
        }
      }, CHAR_MS);
    }, LEAD_MS);

    return () => {
      clearTimeout(kick);
      if (id) clearInterval(id);
    };
  }, [value]);

  const typing = shown.length < (value || "").length;
  return (
    <span className={className} style={style}>
      {shown}
      {typing && <Caret />}
    </span>
  );
}

/**
 * crAIg's reply as it streams in. The text genuinely arrives a piece at a
 * time from the server, so this just renders what has landed.
 */
export function StreamedMd({ text, streaming }: { text: string; streaming?: boolean }) {
  return <Md text={text} caret={streaming} />;
}
